/**
 * Customer Portal Dashboard API Types
 * Zod schemas for API validation
 */

import { z } from '@spcc/core'

// ============================================
// Shared Schemas
// ============================================

export const moneyDtoSchema = z.object({
  amount: z.number(),
  currency: z.enum(['SAR', 'USD']).default('SAR'),
})

export const accountStatusSchema = z.enum(['active', 'suspended', 'blocked'])

// ============================================
// KPI Schemas
// ============================================

export const balanceKpiDtoSchema = z.object({
  creditLimit: z.number(),
  usedBalance: z.number(),
  allowance: z.number(),
  availableBalance: z.number(),
  walletBalance: z.number(),
  accountStatus: accountStatusSchema,
  currency: z.string().default('SAR'),
  vibanNumber: z.string().nullable(),
  paymentTerms: z.string().nullable(),
})

export const ordersKpiDtoSchema = z.object({
  total: z.number(),
  pending: z.number(),
  processing: z.number(),
  delivered: z.number(),
})

export const deliveryKpiDtoSchema = z.object({
  totalTons: z.number(),
  deliveryCount: z.number(),
})

export const financialKpiDtoSchema = z.object({
  totalReceivable: moneyDtoSchema,
  overdueInvoices: z.number(),
  pendingPayments: z.number(),
})

export const summaryKpiDtoSchema = z.object({
  totalQuantityTons: z.number(),
  citiesCount: z.number(),
  customersCount: z.number(),
})

// ============================================
// Chart Data Schemas
// ============================================

export const timeSeriesDataPointSchema = z.object({
  date: z.string(),
  label: z.string(),
  orders: z.number(),
  tons: z.number(),
})

export const chartDataDtoSchema = z.object({
  timeSeries: z.array(timeSeriesDataPointSchema),
  period: z.enum(['daily', 'weekly', 'monthly']),
})

export const chartResponseSchema = z.object({
  data: chartDataDtoSchema,
})

// ============================================
// Rankings Schemas
// ============================================

export const rankingItemDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  tons: z.number(),
  deliveries: z.number(),
})

export const productPackagingRankingItemDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  totalTons: z.number(),
  bulkTons: z.number(),
  bagsTons: z.number(),
  deliveries: z.number(),
})

export const factoryRankingItemDtoSchema = z.object({
  id: z.string(),
  name: z.string(),
  tons: z.number(),
  bulkTons: z.number(),
  bagsTons: z.number(),
  // null on the aggregated "Other" row: a distinct count cannot be summed
  // across factories, so the server sends null rather than a wrong number.
  orderCount: z.number().nullable(),
  productCount: z.number().nullable(),
  deliveries: z.number(),
})

export const rankingsDataDtoSchema = z.object({
  topProducts: z.array(rankingItemDtoSchema),
  topProductsByPackaging: z.array(productPackagingRankingItemDtoSchema),
  topCustomers: z.array(rankingItemDtoSchema),
  topCities: z.array(rankingItemDtoSchema),
  topFactories: z.array(factoryRankingItemDtoSchema).default([]),
  summary: summaryKpiDtoSchema.optional(),
  lastUpdated: z.string(),
})

export const rankingsResponseSchema = z.object({
  data: rankingsDataDtoSchema,
})

export const factoryDetailSummaryDtoSchema = z.object({
  totalTons: z.number(),
  totalOrders: z.number(),
  totalProducts: z.number(),
  totalDeliveries: z.number(),
  bulkTons: z.number(),
  bagsTons: z.number(),
})

export const factoryDetailDataDtoSchema = z.object({
  summary: factoryDetailSummaryDtoSchema,
  topProducts: z.array(productPackagingRankingItemDtoSchema),
  topCustomers: z.array(rankingItemDtoSchema),
  topCities: z.array(rankingItemDtoSchema),
  lastUpdated: z.string(),
})

export const factoryDetailResponseSchema = z.object({
  data: factoryDetailDataDtoSchema,
})

export const entityDetailDataDtoSchema = z.object({
  summary: factoryDetailSummaryDtoSchema,
  topProducts: z.array(productPackagingRankingItemDtoSchema),
  topFactories: z.array(rankingItemDtoSchema),
  topCustomers: z.array(rankingItemDtoSchema),
  topCities: z.array(rankingItemDtoSchema),
  lastUpdated: z.string(),
})

export const entityDetailResponseSchema = z.object({
  data: entityDetailDataDtoSchema,
})

// ============================================
// Aggregated Dashboard Response Schema
// ============================================

export const dashboardDataDtoSchema = z.object({
  balance: balanceKpiDtoSchema,
  orders: ordersKpiDtoSchema,
  deliveries: deliveryKpiDtoSchema,
  financial: financialKpiDtoSchema,
  summary: summaryKpiDtoSchema,
  chartData: chartDataDtoSchema.optional(),
})

export const dashboardResponseSchema = z.object({
  data: dashboardDataDtoSchema,
})

// ============================================
// Live Feed Schemas
// ============================================

export const liveFeedStatusSchema = z.enum(['arrived', 'loading', 'weighing', 'completed'])

export const liveFeedItemSchema = z.object({
  id: z.number(),
  orderNumber: z.string(),
  shipmentNumber: z.string(),
  driverName: z.string(),
  vehiclePlate: z.string(),
  factory: z.string().nullable(),
  customerName: z.string().nullable().optional(),
  loadingLocation: z.string().nullable().optional(),
  product: z.string().nullable(),
  plannedQuantity: z.number(),
  actualWeight: z.number().nullable(),
  status: liveFeedStatusSchema,
  arrivedAt: z.string(),
  lastUpdate: z.string(),
})

export const liveFeedDataDtoSchema = z.object({
  items: z.array(liveFeedItemSchema),
  totalAtFactory: z.number(),
  lastUpdated: z.string(),
})

export const liveFeedResponseSchema = z.object({
  data: liveFeedDataDtoSchema,
})

// ============================================
// Inferred Types
// ============================================

export type MoneyDto = z.infer<typeof moneyDtoSchema>
export type BalanceKpiDto = z.infer<typeof balanceKpiDtoSchema>
export type OrdersKpiDto = z.infer<typeof ordersKpiDtoSchema>
export type DeliveryKpiDto = z.infer<typeof deliveryKpiDtoSchema>
export type FinancialKpiDto = z.infer<typeof financialKpiDtoSchema>
export type SummaryKpiDto = z.infer<typeof summaryKpiDtoSchema>
export type DashboardDataDto = z.infer<typeof dashboardDataDtoSchema>
export type DashboardResponse = z.infer<typeof dashboardResponseSchema>
export type TimeSeriesDataPointDto = z.infer<typeof timeSeriesDataPointSchema>
export type ChartDataDto = z.infer<typeof chartDataDtoSchema>
export type ChartResponse = z.infer<typeof chartResponseSchema>
export type RankingItemDto = z.infer<typeof rankingItemDtoSchema>
export type ProductPackagingRankingItemDto = z.infer<typeof productPackagingRankingItemDtoSchema>
export type FactoryRankingItemDto = z.infer<typeof factoryRankingItemDtoSchema>
export type RankingsDataDto = z.infer<typeof rankingsDataDtoSchema>
export type RankingsResponse = z.infer<typeof rankingsResponseSchema>
export type FactoryDetailSummaryDto = z.infer<typeof factoryDetailSummaryDtoSchema>
export type FactoryDetailDataDto = z.infer<typeof factoryDetailDataDtoSchema>
export type FactoryDetailResponse = z.infer<typeof factoryDetailResponseSchema>
export type EntityDetailDataDto = z.infer<typeof entityDetailDataDtoSchema>
export type EntityDetailResponse = z.infer<typeof entityDetailResponseSchema>
export type LiveFeedItemDto = z.infer<typeof liveFeedItemSchema>
export type LiveFeedDataDto = z.infer<typeof liveFeedDataDtoSchema>
export type LiveFeedResponse = z.infer<typeof liveFeedResponseSchema>

// ============================================
// Validation Helpers
// ============================================

export function validateDashboardResponse(data: unknown): DashboardDataDto {
  const response = dashboardResponseSchema.parse(data)
  return response.data
}

export function validateDashboardData(data: unknown): DashboardDataDto {
  return dashboardDataDtoSchema.parse(data)
}

export function validateChartResponse(data: unknown): ChartDataDto {
  const response = chartResponseSchema.parse(data)
  return response.data
}

export function validateRankingsResponse(data: unknown): RankingsDataDto {
  const response = rankingsResponseSchema.parse(data)
  return response.data
}

export function validateLiveFeedResponse(data: unknown): LiveFeedDataDto {
  const response = liveFeedResponseSchema.parse(data)
  return response.data
}

export function validateFactoryDetailResponse(data: unknown): FactoryDetailDataDto {
  const response = factoryDetailResponseSchema.parse(data)
  return response.data
}

export function validateEntityDetailResponse(data: unknown): EntityDetailDataDto {
  const response = entityDetailResponseSchema.parse(data)
  return response.data
}

// ============================================
// Orders Value KPI Schema
// ============================================

export const ordersValueKpiDtoSchema = z.object({
  totalOrdersValueBeforeTax: z.number().default(0),
  tax: z.number().default(0),
  totalOrdersValueWithTax: z.number().default(0),
  currency: z.string().default('SAR'),
  orderCount: z.number().default(0),
})

export const ordersValueResponseSchema = z.object({
  data: ordersValueKpiDtoSchema,
})

export type OrdersValueKpiDto = z.infer<typeof ordersValueKpiDtoSchema>
export type OrdersValueResponse = z.infer<typeof ordersValueResponseSchema>

export function validateOrdersValueResponse(data: unknown): OrdersValueKpiDto {
  const response = ordersValueResponseSchema.parse(data)
  return response.data
}
